import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableFooter, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { getStaffTips, staffTipsErrorMessage } from '../../../../../services/StaffTipService';
import {
  staffTipWeekRange, validateStaffTipRange, formatTipUSD,
} from '../../../../../utils/staff-tip-utils';
import { useStyles } from './StaffTips.styles';

const dateLabel = (date) => {
  if (!date) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));
};

function hasActivity(summary) {
  return Boolean(summary && (
    summary.order_count > 0 || summary.incomplete || summary.amount === null
    || summary.unknown_amount_rows > 0 || summary.unknown_amount_order_count > 0
    || (typeof summary.amount === 'string' && !/^[+-]?0+\.00$/.test(summary.amount))
    || (typeof summary.known_amount === 'string' && !/^[+-]?0+\.00$/.test(summary.known_amount))
  ));
}

function Summary({ summary, prominent = false }) {
  const classes = useStyles();
  if (!summary) {
    return <Typography className={classes.partial}>Incomplete — summary unavailable</Typography>;
  }
  const incomplete = summary.incomplete || summary.amount === null;
  return (
    <Box>
      <Typography component="div" className={prominent ? classes.amount : classes.cellAmount}>
        {incomplete ? 'Incomplete' : formatTipUSD(summary.amount)}
      </Typography>
      {incomplete && (
        <Typography component="div" className={classes.partial}>
          {summary.known_amount != null
            ? `${formatTipUSD(summary.known_amount)} known (partial)`
            : 'Known amount unavailable'}
          <br />
          {summary.unknown_amount_rows ?? 0} unknown amount row(s)
          {' · '}{summary.unknown_amount_order_count ?? 0} affected order(s)
        </Typography>
      )}
      <Typography component="div" className={classes.count}>
        {summary.order_count} {summary.order_count === 1 ? 'order' : 'orders'}
      </Typography>
    </Box>
  );
}

function ReportTable({ report }) {
  const classes = useStyles();
  const unallocated = (entry) => entry?.periods?.find((period) => period.key === 'unallocated')?.summary;
  const showUnallocated = hasActivity(unallocated(report.totals))
    || report.days.some((day) => hasActivity(unallocated(day)));
  const columns = showUnallocated
    ? [...report.periods, { key: 'unallocated', label: 'Unallocated' }]
    : report.periods;
  const periodSummary = (entry, key) => entry.periods.find((period) => period.key === key)?.summary;

  return (
    <>
      {report.state === 'incomplete' && (
        <Alert severity="warning" className={classes.warning}>
          Some saved tip amounts are unknown. Incomplete cells show only the known
          partial amount, not a complete total.
        </Alert>
      )}
      {showUnallocated && (
        <Alert severity="warning" className={classes.warning}>
          Unallocated tips have a missing or invalid scheduled delivery time.
          They are included in the daily and range totals.
        </Alert>
      )}
      {report.state === 'empty' && (
        <Alert severity="info" className={classes.warning}>
          No recorded tips for this range. All configured periods and requested dates are shown below.
        </Alert>
      )}
      <Box className={classes.panel}>
        <Box className={classes.overview}>
          <Box>
            <Typography className={classes.eyebrow}>RECORDED TIPS · USD</Typography>
            <Summary summary={report.totals.summary} prominent />
          </Box>
          <Box>
            <Typography className={classes.eyebrow}>INCLUSIVE DATE RANGE</Typography>
            <Typography>{dateLabel(report.start_date)} – {dateLabel(report.end_date)}</Typography>
            <Typography className={classes.count}>Scheduled delivery dates · {report.timezone}</Typography>
          </Box>
        </Box>
        <TableContainer tabIndex={0} role="region" aria-label="Daily staff tips; scroll horizontally to see all periods">
          <Table className={classes.table} aria-label="Recorded staff tips by scheduled delivery date">
            <caption style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>
              USD recorded tips. Period starts are inclusive and ends are exclusive.
              24:00 means next midnight. Each total is provided by the server.
            </caption>
            <TableHead>
              <TableRow>
                <TableCell scope="col">Delivery date</TableCell>
                {columns.map((period) => (
                  <TableCell key={period.key} align="right" scope="col">
                    {period.label}
                    <span className={classes.periodTime}>
                      {period.key === 'unallocated'
                        ? 'Missing / invalid time'
                        : `${period.start} ≤ time < ${period.end}`}
                    </span>
                  </TableCell>
                ))}
                <TableCell scope="col" align="right">Daily total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.days.map((day) => (
                <TableRow key={day.date}>
                  <TableCell component="th" scope="row">{dateLabel(day.date)}</TableCell>
                  {columns.map((period) => (
                    <TableCell key={period.key} align="right">
                      <Summary summary={periodSummary(day, period.key)} />
                    </TableCell>
                  ))}
                  <TableCell align="right"><Summary summary={day.summary} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell component="th" scope="row">Range total</TableCell>
                {columns.map((period) => (
                  <TableCell key={period.key} align="right">
                    <Summary summary={periodSummary(report.totals, period.key)} />
                  </TableCell>
                ))}
                <TableCell align="right"><Summary summary={report.totals.summary} /></TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        <Typography className={classes.tableNote}>
          Period starts are inclusive; ends are exclusive. 24:00:00 means next midnight.
          Order counts are distinct within each cell; do not add them together.
        </Typography>
      </Box>
      <Box className={classes.scope}>
        <p>
          Selected saved fees: {report.fees.map((fee) => `${fee.name} (ID ${fee.id})`).join(', ')}.
          {' '}Current fee and period configuration applies to historical dates.
        </p>
      </Box>
    </>
  );
}

function StaffTipsScope({ restaurantId, timezone, onConfiguredChange, onReportError }) {
  const classes = useStyles();
  const [preset, setPreset] = useState('This Week');
  const [custom, setCustom] = useState({ start_date: '', end_date: '' });
  const [retry, setRetry] = useState(0);
  const [resource, setResource] = useState(null);
  const callbackRef = useRef(onConfiguredChange);
  callbackRef.current = onConfiguredChange;
  const errorCallbackRef = useRef(onReportError);
  errorCallbackRef.current = onReportError;
  const week = useMemo(() => {
    if (!timezone) return null;
    try {
      return staffTipWeekRange(timezone, new Date(), preset === 'Last Week' ? -1 : 0);
    } catch (_) {
      return null;
    }
  }, [timezone, preset]);
  const range = preset === 'Custom Date Range' ? custom : week;
  const start = range?.start_date || '';
  const end = range?.end_date || '';
  const validation = !week
    ? 'A valid restaurant timezone is required to load this report.'
    : validateStaffTipRange(start, end);
  const scopeKey = JSON.stringify([restaurantId, timezone, preset, start, end, retry]);
  const activeResource = resource?.key === scopeKey ? resource : null;

  useEffect(() => {
    if (!restaurantId || validation) return undefined;
    const controller = new AbortController();
    let active = true;
    setResource({ key: scopeKey, loading: true });
    getStaffTips(restaurantId, { start_date: start, end_date: end, signal: controller.signal })
      .then((report) => {
        if (!active) return;
        setResource({ key: scopeKey, report });
        if (report.state === 'not_configured') callbackRef.current?.(false);
        else if (report.configured) callbackRef.current?.(true);
      })
      .catch((error) => {
        if (!active || controller.signal.aborted) return;
        setResource({ key: scopeKey, error });
        errorCallbackRef.current?.(error);
      });
    return () => { active = false; controller.abort(); };
  }, [restaurantId, timezone, start, end, validation, scopeKey]);

  const changePreset = (event) => {
    if (event.target.value === 'Custom Date Range') {
      setCustom({ start_date: start, end_date: end });
    }
    setPreset(event.target.value);
  };
  const report = activeResource?.report;
  const error = activeResource?.error;
  const friendlyError = error ? staffTipsErrorMessage(error) : null;
  const errorText = typeof friendlyError === 'string'
    ? friendlyError : friendlyError?.message || 'The staff tips report could not be loaded.';
  const reference = error?.request_id || error?.requestId
    || error?.response?.data?.request_id || friendlyError?.request_id;

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box>
          <Typography variant="h1">Staff Tips</Typography>
          <Typography className={classes.subtitle}>
            Recorded tips · USD · {timezone || 'Restaurant timezone unavailable'}
          </Typography>
        </Box>
        <Box className={classes.filters}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="staff-tips-range-label">Date range</InputLabel>
            <Select labelId="staff-tips-range-label" label="Date range" value={preset} onChange={changePreset}>
              {['This Week', 'Last Week', 'Custom Date Range'].map((label) => (
                <MenuItem key={label} value={label}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      {preset === 'Custom Date Range' && (
        <Box className={classes.dates}>
          <TextField label="Start date (inclusive)" type="date" size="small"
            InputLabelProps={{ shrink: true }} value={custom.start_date}
            onChange={(event) => {
              const value = event.target.value;
              setCustom((old) => ({ ...old, start_date: value }));
            }} />
          <TextField label="End date (inclusive)" type="date" size="small"
            InputLabelProps={{ shrink: true }} value={custom.end_date}
            onChange={(event) => {
              const value = event.target.value;
              setCustom((old) => ({ ...old, end_date: value }));
            }} />
          <Typography className={classes.count}>Up to 366 calendar days, inclusive.</Typography>
        </Box>
      )}
      {validation ? (
        <Alert severity="warning" className={classes.warning}>{validation}</Alert>
      ) : !restaurantId ? (
        <Alert severity="info" className={classes.warning}>Select a restaurant to view recorded staff tips.</Alert>
      ) : error ? (
        <Alert severity="error" className={classes.warning}
          action={<Button color="inherit" onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>
          {errorText}
          {reference && <Typography variant="caption" display="block">Reference: {String(reference)}</Typography>}
        </Alert>
      ) : !report ? (
        <Box className={classes.panel} padding={3} aria-busy="true" aria-label="Loading staff tips">
          <Skeleton width="35%" height={32} />
          <Skeleton width="48%" height={64} />
          {[0, 1, 2, 3, 4, 5, 6].map((row) => <Skeleton key={row} height={48} />)}
        </Box>
      ) : report.state === 'not_configured' ? (
        <Box className={`${classes.panel} ${classes.state}`}>
          <Typography variant="h2">Staff tips reporting is not configured</Typography>
          <Typography variant="body2" color="text.secondary">
            No tip fees or reporting periods have been selected for this restaurant.
            Ask your administrator to review the setup. This is not a zero-tip report.
          </Typography>
        </Box>
      ) : <ReportTable report={report} />}
      <Box className={classes.scope}>
        <p>
          Recorded tips on completed orders, grouped by scheduled restaurant-local
          delivery date and time — not actual fulfillment time.
          This is not payroll, a payout calculation, or refund-adjusted reporting.
        </p>
        <p>
          Current configuration applies to all historical dates.
          Amounts and distinct order counts come directly from server summaries.
        </p>
      </Box>
    </Box>
  );
}

export default function StaffTips(props) {
  return <StaffTipsScope key={`${props.restaurantId || ''}:${props.timezone || ''}`} {...props} />;
}
